import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import { affiliatesV2API } from '../../services/api/v2/affiliates';
import { productsV2API } from '../../services/api/v2/products';
import AffiliateInviteDrawer from './AffiliateInviteDrawer';

/**
 * AffiliateInviteBanner - Banner que aparece no topo do dashboard
 * quando o usuário tem convites de afiliado pendentes.
 *
 * V2: usa product_affiliates com status='pending' (não há mais tabela affiliate_invites
 * separada). Aceitar = approve(id). Recusar = reject(id, { reason }).
 */
const AffiliateInviteBanner = () => {
  const [invites, setInvites] = useState([]);
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  /**
   * Enriquece um vínculo V2 com dados do produto e do manager (produtor)
   * pra alimentar o shape esperado pelo Drawer.
   */
  const enrichLink = useCallback(async (link) => {
    let product = null;
    try {
      const res = await productsV2API.findById(link.product_id);
      product = res.data?.product || res.data?.data || res.data;
    } catch (err) {
      console.warn(`Erro carregando produto ${link.product_id}:`, err.message);
    }

    return {
      id: link.id,
      token: link.id,
      product_image: product?.image_url || product?.image || null,
      product_name: product?.name || 'Produto',
      product_uuid: product?.uuid || null,
      manager_name: product?.producer_name || 'Produtor',
      manager_email: product?.producer_email || null,
      commission_rate: link.commission_value_override ?? product?.commission_value_affiliate ?? 0,
      commission_type: product?.commission_type || 'percent',
      expires_at: null,
      created_at: link.created_at,
    };
  }, []);

  const fetchInvites = useCallback(async () => {
    try {
      const res = await affiliatesV2API.listMine({ status: 'pending' });
      const list = res.data?.links || res.data?.data || [];
      const enriched = await Promise.all(list.map(enrichLink));
      setInvites(enriched);
    } catch (error) {
      console.error('Erro ao buscar convites:', error);
    }
  }, [enrichLink]);

  // Buscar status de requisitos (V1 ainda — tabela inalterada)
  const fetchRequirements = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/affiliate-requirements-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setRequirements(data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar requisitos:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchInvites(), fetchRequirements()]);
      setLoading(false);
    };

    fetchData();
  }, [fetchInvites, fetchRequirements]);

  /**
   * Aceitar = approve do vínculo.
   * O parâmetro `tokenOrId` vem do Drawer como `invite.token`, mas
   * setamos `token = invite.id` no enrichLink — então é o ID mesmo.
   */
  const handleAcceptInvite = async (tokenOrId) => {
    try {
      await affiliatesV2API.approve(tokenOrId);
      await fetchInvites();
      return { success: true, message: 'Convite aceito com sucesso!' };
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao aceitar convite'
      };
    }
  };

  const handleRejectInvite = async (tokenOrId) => {
    try {
      await affiliatesV2API.reject(tokenOrId, { reason: 'Recusado pelo afiliado' });
      await fetchInvites();
      return { success: true, message: 'Convite recusado.' };
    } catch (error) {
      console.error('Erro ao recusar convite:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao recusar convite'
      };
    }
  };

  if (loading || dismissed || invites.length === 0) {
    return null;
  }

  const inviteCount = invites.length;

  return (
    <>
      <div
        className="affiliate-invite-banner"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 20px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px',
          boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon icon="mdi:account-plus" style={{ fontSize: '22px' }} />
          </div>
          <div>
            <span style={{ fontWeight: '600', fontSize: '15px' }}>
              {inviteCount === 1
                ? 'Você tem 1 convite de afiliado pendente!'
                : `Você tem ${inviteCount} convites de afiliado pendentes!`
              }
            </span>
            <span
              style={{
                display: 'block',
                fontSize: '13px',
                opacity: 0.9,
                marginTop: '2px'
              }}
            >
              Clique para ver detalhes e aceitar
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => setShowDrawer(true)}
            style={{
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <Icon icon="mdi:eye" style={{ fontSize: '18px' }} />
            Ver Convites
          </button>

          <button
            onClick={() => setDismissed(true)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255,255,255,0.2)';
            }}
            title="Dispensar temporariamente"
          >
            <Icon icon="mdi:close" style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>

      <AffiliateInviteDrawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        invites={invites}
        requirements={requirements}
        onAccept={handleAcceptInvite}
        onReject={handleRejectInvite}
        onRefresh={fetchInvites}
      />
    </>
  );
};

export default AffiliateInviteBanner;
